// โหมด direct — วงจรชีวิตของ 1 session กับ cpac-livestream-service

import { useCallback, useEffect, useReducer, useRef } from 'react'
import {
  CONNECT_GRACE_MS,
  CONNECT_TIMEOUT_FALLBACK_SEC,
  END_REASON,
  EXPIRED_REOPEN_MIN_GAP_MS,
  POLL_FALLBACK_MS,
  POLL_MIN_MS,
  QUALITY,
  SESSION,
} from './constants'
import {
  apiError,
  createSession,
  getSession,
  noop,
  stopSession,
  stopSessionOnUnload,
} from './livestreamApi'
import { PHASE, pickCamera, toCellError } from './cellState'
import { recallCamera, rememberCamera } from './cameraMemory'
import useHeartbeat from './useHeartbeat'
import usePageVisible from './usePageVisible'

const isSet = v => v !== null && v !== undefined

const INITIAL = {
  phase: PHASE.IDLE,
  runKey: null, // ไม่ตรงกับรอบปัจจุบัน = state ของเก่า ห้ามเอาไปเล่น
  vehicleId: null,
  discovery: null,
  camera: null,
  session: null, // { id, heartbeatSec, released }
  stream: null, // { url, protocol, expires_at }
  error: null,
  endReason: null,
  reopen: false, // จบเพราะสตรีมครบอายุระหว่างเล่น → เปิด session ใหม่ให้เอง
}

/** backend ส่ง heartbeat_sec มาได้ทั้งตอนสร้าง session และใน snapshot ที่ ready */
function withHeartbeat(session, sec) {
  if (!session || typeof sec !== 'number' || sec === session.heartbeatSec) {
    return session
  }
  return { ...session, heartbeatSec: sec }
}

function reducer(state, action) {
  switch (action.type) {
    case 'reset':
      return INITIAL
    case 'discovering': {
      // รถคันเดิม (retry / เปลี่ยนกล้อง) คงรายการกล้องไว้ ไม่งั้น dropdown หายจนกว่า discovery จะตอบ
      const prev = state.vehicleId === action.vehicleId ? state : INITIAL
      return {
        ...INITIAL,
        phase: PHASE.DISCOVERING,
        runKey: action.runKey,
        vehicleId: action.vehicleId,
        discovery: prev.discovery,
        camera: isSet(action.camera) ? action.camera : prev.camera,
      }
    }
    case 'discovered':
      return {
        ...state,
        discovery: action.discovery,
        camera: action.camera,
      }
    case 'creating':
      return { ...state, phase: PHASE.CREATING }
    case 'created':
      return { ...state, session: action.session }
    case 'polling':
      return state.phase === PHASE.POLLING
        ? state
        : { ...state, phase: PHASE.POLLING }
    case 'ready':
      return {
        ...state,
        phase: PHASE.READY,
        session: withHeartbeat(state.session, action.heartbeatSec),
        stream: action.stream,
        error: null,
      }
    case 'stream':
      return { ...state, stream: action.stream }
    case 'released':
      // DELETE ไปแล้ว — เก็บ id ไว้ให้ปุ่ม "คัดลอกส่ง IT" แต่เลิก heartbeat
      return state.session
        ? { ...state, session: { ...state.session, released: true } }
        : state
    case 'failed':
      // URL ที่ค้างอยู่ห้ามเล่นต่อหลังกรอบขึ้น error
      return {
        ...state,
        phase: PHASE.FAILED,
        error: action.error,
        stream: null,
      }
    case 'ended':
      // จบได้ครั้งเดียว — heartbeat กับ GET ที่ค้างอยู่อาจรายงานซ้ำ
      if (state.phase === PHASE.ENDED) return state
      return {
        ...state,
        phase: PHASE.ENDED,
        endReason: action.reason || action.fallback || END_REASON.ERROR,
        // เฉพาะที่ backend บอกเองตอนกำลังเล่น — 404 ที่หน้าเว็บเดาว่าหมดอายุไม่นับ
        // expired = สตรีมครบอายุ · idle_timeout = heartbeat ไม่ถึง (แท็บถูกซ่อน) ทั้งที่กรอบยังเปิดอยู่
        reopen:
          (action.reason === END_REASON.EXPIRED ||
            action.reason === END_REASON.IDLE_TIMEOUT) &&
          state.phase === PHASE.READY,
        stream: null,
      }
    default:
      return state
  }
}

function pollDelay(ms) {
  const n = Number(ms)
  return n ? Math.max(n, POLL_MIN_MS) : POLL_FALLBACK_MS
}

/** error ชั่วคราว — ระหว่างรอ ready ให้ poll ต่อ แทนการล้มทั้งรอบแล้วสั่งกล้องใหม่ */
function isTransient(err) {
  if (!err) return false
  if (['network_error', 'timeout', 'rate_limited'].indexOf(err.code) !== -1) {
    return true
  }
  return err.status >= 500
}

function retryDelay(err) {
  return err && err.retryAfterSec ? err.retryAfterSec * 1000 : POLL_FALLBACK_MS
}

function isSessionGone(err) {
  return (
    !!err &&
    (err.code === 'session_ended' ||
      err.code === 'session_not_found' ||
      err.status === 410 ||
      err.status === 404)
  )
}

/** session ตายฝั่ง backend แล้ว → จบรอบโดยไม่ DELETE ซ้ำ · คืน true = จัดการแล้ว */
function endIfGone(run, err, dispatch) {
  if (run.cancelled || !isSessionGone(err)) return false
  run.sessionId = null
  dispatch({
    type: 'ended',
    reason: err.endReason,
    fallback: END_REASON.EXPIRED,
  })
  return true
}

/** discovery = คำตอบถามกล้องของกรอบ (useCameraDiscovery) · camera = ล็อกกล้อง (กรอบที่เปิดทุกกล้อง) */
export default function useDirectSession({
  enabled,
  vehicleId,
  discovery: given,
  camera: fixedCamera,
}) {
  const [state, dispatch] = useReducer(reducer, INITIAL)
  const [runKey, bump] = useReducer(n => n + 1, 0)

  // ค่าที่ผู้ใช้ "อยากได้" — อ่านตอนเริ่มรอบใหม่ ไม่ใช่ dependency
  const prefs = useRef({ camera: null })

  // ให้ cleanup / heartbeat / การกระทำของผู้ใช้เห็น session id ล่าสุด
  const run = useRef({ cancelled: true, sessionId: null, pollTimer: null })

  const stateRef = useRef(state)
  stateRef.current = state

  const active = enabled && isSet(vehicleId) && !!given

  // ผูกกับ runKey ไม่ใช่ camera ตรง ๆ เพราะ discovery เป็นคนเติมค่านี้
  // ถ้าผูกตรง ๆ จะวนรอบใหม่ทันทีที่ discovery ตอบ
  useEffect(() => {
    if (!active) {
      dispatch({ type: 'reset' })
      return undefined
    }

    const me = { cancelled: false, sessionId: null, pollTimer: null }
    run.current = me
    const wanted = { ...prefs.current }
    if (isSet(fixedCamera)) wanted.camera = fixedCamera

    dispatch({
      type: 'discovering',
      runKey,
      vehicleId,
      camera: wanted.camera,
    })

    // backend นับคนดูตั้งแต่สร้าง session → ล้มแล้วต้อง DELETE ทันที ไม่ใช่รอถอดกรอบ
    // ไม่งั้นกล้องส่งภาพค้างจน backend หมดเวลาเอง
    function giveUp() {
      if (!me.sessionId) return
      stopSession(me.sessionId).catch(noop)
      me.sessionId = null
      dispatch({ type: 'released' })
    }

    function fail(err) {
      if (me.cancelled) return
      dispatch({ type: 'failed', error: toCellError(err) })
      giveUp()
    }

    function settle(snap, deadline) {
      if (me.cancelled || !snap) return

      if (snap.state === SESSION.READY) {
        if (!snap.stream || !snap.stream.url) {
          fail(
            apiError('vendor_error', 'backend ตอบ ready แต่ไม่มี stream.url'),
          )
          return
        }
        dispatch({
          type: 'ready',
          stream: snap.stream,
          heartbeatSec: snap.heartbeat_sec,
        })
        return
      }
      if (snap.state === SESSION.FAILED) {
        dispatch({
          type: 'failed',
          error: toCellError(snap.error, 'vendor_error'),
        })
        giveUp()
        return
      }
      if (snap.state === SESSION.ENDED) {
        dispatch({ type: 'ended', reason: snap.end_reason })
        return
      }

      // เพดานกันไว้เผื่อ backend เงียบ — ปกติ backend เปลี่ยนเป็น failed เองเมื่อครบ connect_timeout_sec
      if (Date.now() > deadline) {
        fail(apiError('vendor_timeout', 'รอ backend ยืนยันภาพนานเกินกำหนด'))
        return
      }
      pollAfter(pollDelay(snap.poll_after_ms), deadline)
    }

    function pollAfter(delayMs, deadline) {
      dispatch({ type: 'polling' })
      me.pollTimer = setTimeout(() => {
        getSession(me.sessionId)
          .then(snap => settle(snap, deadline))
          .catch(err => {
            if (me.cancelled) return
            if (endIfGone(me, err, dispatch)) return
            if (isTransient(err) && Date.now() < deadline) {
              pollAfter(retryDelay(err), deadline)
              return
            }
            fail(err)
          })
      }, delayMs)
    }

    // ลองใหม่ / เปลี่ยนกล้อง / เปิดใหม่เอง ใช้คำตอบเดิม — cpac ตรวจกล่องซ้ำตอนเปิดอยู่แล้ว
    Promise.resolve(given)
      .then(found => {
        if (me.cancelled) return null

        const discovery = found || {}
        const camera = pickCamera(
          discovery,
          wanted.camera,
          recallCamera(vehicleId),
        )
        dispatch({ type: 'discovered', discovery, camera })

        if (discovery.online === false) {
          throw apiError('device_offline', 'อุปกรณ์ไม่ได้เชื่อมต่อ')
        }
        // ระบบกลางสั่งเปิดเฉพาะกล่องที่ vendor บอกว่าออนไลน์
        if (discovery.online !== true) {
          throw apiError(
            'device_status_unknown',
            'vendor ไม่บอกว่าอุปกรณ์ออนไลน์ไหม',
          )
        }
        if (camera === null) {
          throw apiError('camera_not_found', 'ไม่มีข้อมูลกล้องของรถคันนี้', {
            retryable: false,
          })
        }

        dispatch({ type: 'creating' })
        // ขอ sub เสมอ ประหยัดเน็ต 4G ของรถ ผู้ใช้เลือก main ไม่ได้
        return createSession({ vehicleId, camera, quality: QUALITY.SUB })
      })
      .then(created => {
        if (me.cancelled) {
          // ตอบกลับหลังกรอบเลิกใช้รอบนี้แล้ว ต้องสั่งหยุด ไม่งั้น backend
          // นับว่ายังมีคนดูอยู่ 1 คน แล้วไม่สั่งกล้องหยุดเลย
          if (created && created.session_id) {
            stopSession(created.session_id).catch(noop)
          }
          return
        }
        if (!created || !created.session_id) {
          throw apiError('vendor_error', 'POST /sessions ไม่คืน session_id')
        }

        me.sessionId = created.session_id
        dispatch({
          type: 'created',
          session: {
            id: created.session_id,
            heartbeatSec: created.heartbeat_sec || 0,
          },
        })

        const timeoutSec =
          created.connect_timeout_sec || CONNECT_TIMEOUT_FALLBACK_SEC
        settle(created, Date.now() + timeoutSec * 1000 + CONNECT_GRACE_MS)
      })
      .catch(fail)

    return () => {
      me.cancelled = true
      if (me.pollTimer) clearTimeout(me.pollTimer)
      if (me.sessionId) {
        stopSession(me.sessionId).catch(noop)
        me.sessionId = null
      }
    }
  }, [active, vehicleId, runKey, given, fixedCamera])

  // ปิดแท็บแล้ว fetch ไปไม่ถึง ต้องใช้ beacon
  useEffect(() => {
    const onUnload = () => stopSessionOnUnload(run.current.sessionId)
    window.addEventListener('pagehide', onUnload)
    return () => window.removeEventListener('pagehide', onUnload)
  }, [])

  const ready = state.phase === PHASE.READY
  const live = ready && !!state.session && !state.session.released
  useHeartbeat(
    live ? state.session.id : null,
    state.session ? state.session.heartbeatSec : 0,
    {
      onTick: res => {
        if (res && res.stream && res.stream.url) {
          dispatch({ type: 'stream', stream: res.stream })
        }
      },
      // เน็ตกระตุก / timeout ปล่อยผ่าน รอบหน้าค่อยส่งใหม่ — จบเฉพาะเมื่อ session ตายแล้ว
      onFail: err => endIfGone(run.current, err, dispatch),
    },
  )

  // สตรีมครบอายุ / ถูกตัดเพราะ heartbeat ไม่ถึง ระหว่างดู → เปิดใหม่เอง แต่ถ้าซ้ำเร็วเกินไป
  // (vendor ตั้งอายุสั้นผิดปกติ / แท็บถูกซ่อนซ้ำ ๆ) หยุดไว้ให้ผู้ใช้กดเอง
  // รอแท็บกลับมาแสดงก่อน — เปิดตอนซ่อนอยู่ heartbeat ก็ถูกหน่วงจนถูกตัดอีก
  const visible = usePageVisible()
  const lastReopen = useRef({ vehicleId: null, at: 0 })
  const reopenDue =
    state.phase === PHASE.ENDED && state.reopen && state.runKey === runKey
  useEffect(() => {
    if (!reopenDue || !visible) return
    const now = Date.now()
    const last = lastReopen.current
    if (
      last.vehicleId === vehicleId &&
      now - last.at < EXPIRED_REOPEN_MIN_GAP_MS
    ) {
      return
    }
    lastReopen.current = { vehicleId, at: now }
    bump()
  }, [reopenDue, visible, vehicleId])

  const retry = useCallback(() => bump(), [])

  /** เปลี่ยนกล้อง = session ใหม่เสมอ */
  const changeCamera = useCallback(
    camera => {
      prefs.current.camera = camera
      if (isSet(vehicleId)) rememberCamera(vehicleId, camera)
      bump()
    },
    [vehicleId],
  )

  // player พังถาวร → DELETE ทันที ไม่งั้น heartbeat ยังวิ่ง กล้องส่งภาพค้างให้จอที่ขึ้น error
  const release = useCallback(() => {
    const me = run.current
    if (!me.sessionId) return
    stopSession(me.sessionId).catch(noop)
    me.sessionId = null
    dispatch({ type: 'released' })
  }, [])

  // ขอ URL ใหม่โดยไม่สร้าง session (token ใน URL อาจหมดอายุ)
  // คืน true เฉพาะเมื่อได้ URL ต่างจากเดิม — URL เดิมแปลว่าขอใหม่ไม่ช่วย
  const refreshStream = useCallback(() => {
    const me = run.current
    if (!me.sessionId) return Promise.resolve(false)

    return getSession(me.sessionId)
      .then(snap => {
        if (me.cancelled || !snap) return false
        if (snap.state === SESSION.ENDED) {
          me.sessionId = null
          dispatch({ type: 'ended', reason: snap.end_reason })
          return false
        }
        if (snap.state !== SESSION.READY || !snap.stream || !snap.stream.url) {
          return false
        }
        const current = stateRef.current.stream
        if (current && current.url === snap.stream.url) return false

        dispatch({ type: 'stream', stream: snap.stream })
        return true
      })
      .catch(err => {
        endIfGone(me, err, dispatch)
        return false
      })
  }, [])

  // กด retry / เปลี่ยนกล้องแล้วแต่ effect รอบใหม่ยังไม่รัน: stream ใน state เป็นของ session
  // ที่กำลังถูก DELETE — ห้ามส่งให้ player ไม่งั้นจะ mount player ทิ้ง ๆ 1 ตัว
  const current = state.runKey === runKey

  return {
    phase: state.phase,
    /** เพิ่มทุกครั้งที่เริ่มรอบใหม่ — ไว้บังคับ player สร้างใหม่แม้ URL เดิม */
    attempt: runKey,
    /** undefined ก่อน discovery ตอบ · null = vendor บอกไม่ได้ */
    deviceOnline: state.discovery ? state.discovery.online : undefined,
    cameras: (state.discovery && state.discovery.cameras) || [],
    camera: state.camera,
    session: state.session,
    stream: current ? state.stream : null,
    error: state.error,
    endReason: state.endReason,
    retry,
    changeCamera,
    refreshStream,
    release,
  }
}
